import React, { useState, useRef, useEffect, useCallback } from 'react';
import { debounce } from 'lodash-es';
import Icon from '../AppIcon';

const SearchInterface = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const inputRef = useRef(null);

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'stories', label: 'Stories' },
    { id: 'writers', label: 'Writers' },
    { id: 'topics', label: 'Topics' },
  ];

  const mockResults = [
    {
      id: 1,
      type: 'story',
      title: 'The Future of UK Publishing in 2026',
      author: 'James Thompson',
      excerpt: 'Exploring how digital platforms are reshaping the literary landscape...',
      readTime: '8 min read',
    },
    {
      id: 2,
      type: 'story',
      title: 'AI-Assisted Writing: A Writer\'s Perspective',
      author: 'Emma Clarke',
      excerpt: 'My journey using AI tools to enhance creative writing without losing authenticity...',
      readTime: '6 min read',
    },
    {
      id: 3,
      type: 'writer',
      name: 'Sarah Mitchell',
      bio: 'Literary fiction writer | 2.5k followers',
      stories: 24,
    },
  ];

  // Debounced search handler - waits 300ms after user stops typing
  const debouncedSearch = useCallback(
    debounce((query) => {
      setDebouncedQuery(query);
      // Here you would typically make an API call with the debounced query
      console.log('Searching for:', query);
    }, 300),
    []
  );

  useEffect(() => {
    inputRef?.current?.focus();

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      debouncedSearch?.cancel(); // Cancel pending debounced calls on unmount
    };
  }, [onClose, debouncedSearch]);

  const handleSearch = (e) => {
    const query = e?.target?.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  return (
    <div className="search-overlay">
      <div className="search-overlay-container">
        <div className="search-overlay-header">
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search stories, writers, topics..."
            className="search-overlay-input"
          />
          <button
            onClick={onClose}
            className="search-overlay-close"
            aria-label="Close search"
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="search-filters">
          {filters?.map((filter) => (
            <button
              key={filter?.id}
              onClick={() => setActiveFilter(filter?.id)}
              className={`search-filter-chip ${activeFilter === filter?.id ? 'active' : ''}`}
            >
              {filter?.label}
            </button>
          ))}
        </div>

        <div className="search-results">
          {searchQuery?.length > 0 ? (
            mockResults?.map((result) => (
              <div
                key={result?.id}
                className="search-result-item"
                onClick={() => {
                  console.log('Navigate to:', result);
                  onClose();
                }}
              >
                {result?.type === 'story' ? (
                  <>
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                      {result?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      by {result?.author} · {result?.readTime}
                    </p>
                    <p className="text-foreground line-clamp-2">{result?.excerpt}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-interface font-semibold">
                        {result?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </div>
                      <div>
                        <h3 className="text-lg font-interface font-semibold text-foreground">
                          {result?.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">{result?.bio}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{result?.stories} stories published</p>
                  </>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground font-interface">
                Start typing to search stories, writers, and topics
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchInterface;